/**
 * <als-countdown> — visual countdown timer for the product page.
 *
 * Reads the duration from data-minutes. The end timestamp is stored in
 * localStorage per visitor, so a refresh or a variant re-render keeps the
 * remaining time instead of restarting it. When it reaches zero the element
 * hides itself and stays hidden for that visitor.
 *
 * Storage is optional: if it throws (private window, blocked site data) the
 * timer still runs, it just restarts on the next page load.
 */
class AlsCountdown extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    const minutes = parseInt(this.dataset.minutes, 10) || 30;
    this.duration = minutes * 60 * 1000;
    this.storageKey = this.dataset.storageKey || 'als_countdown_end_' + minutes;
    this.renewalDuration = (parseInt(this.dataset.renewalSeconds, 10) || 10) * 1000;
    this.units = {
      h: this.querySelector('[data-unit="h"]'),
      m: this.querySelector('[data-unit="m"]'),
      s: this.querySelector('[data-unit="s"]'),
    };

    this.clock = this.querySelector('[data-countdown-clock]');
    this.renewal = this.querySelector('[data-countdown-renewal]');
    this.end = this.readEnd();
    this.onVisible = () => {
      if (!document.hidden) this.tick();
    };
    document.addEventListener('visibilitychange', this.onVisible);

    this.startTimer();
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    clearTimeout(this.transitionTimer);
    clearTimeout(this.renewalTimer);
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  readEnd() {
    const now = Date.now();
    try {
      const stored = parseInt(window.localStorage.getItem(this.storageKey), 10);
      // Ignore values that could not have come from this timer (edited or from
      // a longer duration) so the clock never shows more than its full span.
      if (stored && stored > now && stored <= now + this.duration) return stored;
      return this.saveEnd(now + this.duration);
    } catch (e) {
      return now + this.duration;
    }
  }

  saveEnd(end) {
    try {
      window.localStorage.setItem(this.storageKey, String(end));
    } catch (e) {
      // The timer still works for this page when storage is unavailable.
    }
    return end;
  }

  startTimer() {
    clearInterval(this.timer);
    this.timer = setInterval(() => this.tick(), 1000);
    this.tick();
  }

  /** Returns true while time remains. */
  tick() {
    const left = Math.max(0, this.end - Date.now());
    if (left === 0) {
      clearInterval(this.timer);
      this.showRenewal();
      return;
    }
    const total = Math.ceil(left / 1000);
    const pad = (n) => String(n).padStart(2, '0');
    this.write('h', pad(Math.floor(total / 3600)));
    this.write('m', pad(Math.floor((total % 3600) / 60)));
    this.write('s', pad(total % 60));
    this.dataset.ready = '';
  }

  showRenewal() {
    this.dataset.state = 'transitioning';
    this.transitionTimer = setTimeout(() => {
      this.clock?.setAttribute('hidden', '');
      this.renewal?.removeAttribute('hidden');
      this.dataset.state = 'renewing';
      this.renewalTimer = setTimeout(() => {
        this.dataset.state = 'returning';
        this.transitionTimer = setTimeout(() => {
          this.renewal?.setAttribute('hidden', '');
          this.clock?.removeAttribute('hidden');
          delete this.dataset.state;
          this.end = this.saveEnd(Date.now() + this.duration);
          this.startTimer();
        }, 160);
      }, this.renewalDuration);
    }, 180);
  }

  write(unit, text) {
    const el = this.units[unit];
    if (el && el.textContent !== text) el.textContent = text;
  }
}

if (!customElements.get('als-countdown')) {
  customElements.define('als-countdown', AlsCountdown);
}
